<template>
  <div class="container-max-width">
    <p v-if="title" class="accent title">
      {{ title }}
    </p>

    <div class="wrapper-control">
      <div class="wrapper-width">
        <button
          v-if="usingButton"
          class="button-primary button-small button-rounded button-icon"
          :disabled="isDisabledDecrease"
          @click="decreaseValue"
        >
          <IconMinus class="icon-smaller" />
        </button>

        <div class="numeric-input-wrapper">
          <div class="numeric-input">
            <input
              :value="inputValue"
              :class="{ 'input-error': errorMessage }"
              :disabled="disabled"
              :placeholder="placeholder"
              inputmode="decimal"
              autocomplete="off"
              spellcheck="false"
              type="number"
              @input="handleInput"
            />
            <span class="unit">{{ unit }}</span>
          </div>
        </div>

        <button
          v-if="usingButton"
          class="button-primary button-small button-rounded button-icon"
          :disabled="isDisabledIncrease"
          @click="increaseValue"
        >
          <IconPlus class="icon-smaller" />
        </button>
      </div>
      <div v-if="errorMessage" class="error-message">
        <IconInfo class="icon-smaller icon-error" />
        <p class="small error">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import IconInfo from '@/components/icons/IconInfo.vue';
import IconMinus from '@/components/icons/IconMinus.vue';
import IconPlus from '@/components/icons/IconPlus.vue';

const modelValue = defineModel<number>({ required: true, default: 0 });

const props = withDefaults(
  defineProps<{
    title?: string;
    unit?: string;

    min?: number;
    max?: number;
    disabled?: boolean;
    placeholder?: string;

    increment?: number;
    usingButton?: boolean;
    validate?: (val: number) => string;
  }>(),
  {
    title: '',
    unit: '',

    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    disabled: false,
    placeholder: '',

    increment: 10,
    usingButton: false,

    validate: (val: number) => '',
  }
);

const inputValue = ref<string>('');
const errorMessage = ref<string>('');

const isDisabledDecrease = computed<boolean>(() => {
  return Number(inputValue.value) <= props.min;
});

const isDisabledIncrease = computed<boolean>(() => {
  return Number(inputValue.value) >= props.max;
});

const initInputValue = () => {
  inputValue.value = modelValue.value.toString();
};

const updateValue = (delta: number) => {
  errorMessage.value = '';
  modelValue.value = formatValue(Number(inputValue.value) + delta);
  initInputValue();
};

const increaseValue = () => {
  updateValue(props.increment);
};

const decreaseValue = () => {
  updateValue(-props.increment);
};

const formatValue = (value: number): number => {
  if (value < props.min) {
    return props.min;
  }
  if (value > props.max) {
    return props.max;
  }
  return value;
};

const handleInput = (e) => {
  errorMessage.value = '';
  inputValue.value = e.target.value;

  const num = Number(inputValue.value);
  const message = props.validate(num);

  if (message) {
    errorMessage.value = message;
  } else {
    modelValue.value = num;
  }
};

watch(
  () => modelValue.value,
  () => {
    initInputValue();
  },
  { immediate: true }
);
</script>

<style scoped>
.container-max-width {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  gap: 0.5rem;
}

.title {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.wrapper-width {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

input[type='number'] {
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.numeric-input-wrapper {
  .numeric-input {
    position: relative;
    display: flex;

    .input-error {
      border-color: var(--error);
      box-shadow: none;
    }

    .unit {
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--font-dim);
      pointer-events: none;
    }
  }
}
.error-message {
  width: 100%;
  display: flex;
  justify-content: left;
  margin-top: 0.3rem;
  word-break: keep-all;
  gap: 12px;
  border-radius: 8px;

  .icon-error {
    position: relative;
    top: 0.125rem;
  }
}
</style>
